import { Tooltip } from '@/components/ui/tooltip';
import { chakra, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

/**
 * A monthly allowance of the free plan, as a pill placed where the allowance
 * is spent: the count in bold, then what it counts ("12 / 15 searches this
 * month"). The tooltip says when it resets and what counts.
 *
 * The pill sells nothing while the allowance lasts: the plans live in the menu
 * card. Once it is used it turns amber and becomes the way to the plan that
 * lifts it, which is the only moment the brand is stopped.
 */
export const PlanUsageCounter = ({
  used,
  limit,
  unit,
  exhaustedLabel,
  icon,
  accent,
  hint,
  onUpgrade,
}: {
  used: number;
  limit: number;
  /** What the count counts, after the bold count ("searches this month"). */
  unit: string;
  /** Replaces the count and unit once the allowance is used, when the page says more. */
  exhaustedLabel?: string;
  icon: ReactNode;
  /** Pill background while the allowance lasts. */
  accent: string;
  /** What the tooltip explains (reset day, what counts). */
  hint?: ReactNode;
  onUpgrade: () => void;
}) => {
  const isExhausted = used >= limit;
  const content = (
    <>
      <chakra.span display="inline-flex" flex="none" aria-hidden="true">
        {icon}
      </chakra.span>
      {isExhausted && exhaustedLabel ? (
        <Text as="span">{exhaustedLabel}</Text>
      ) : (
        <Text as="span">
          <chakra.strong fontFamily="heading" fontVariantNumeric="tabular-nums">
            {Math.min(used, limit)} / {limit}
          </chakra.strong>{' '}
          {unit}
        </Text>
      )}
    </>
  );
  const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    minH: '30px',
    px: '12px',
    py: '4px',
    rounded: 'full',
    fontSize: '12.5px',
    lineHeight: '1.3',
    textAlign: 'left',
    color: isExhausted ? 'warning.text' : 'ink',
    bg: isExhausted ? 'warning.surface' : accent,
    'data-testid': 'plan-usage-pill',
  } as const;

  return (
    <Tooltip content={hint} disabled={!hint} showArrow>
      {isExhausted ? (
        <chakra.button
          type="button"
          {...pill}
          cursor="pointer"
          _hover={{ filter: 'brightness(0.97)' }}
          onClick={onUpgrade}
        >
          {content}
        </chakra.button>
      ) : (
        <chakra.span {...pill} tabIndex={hint ? 0 : undefined}>
          {content}
        </chakra.span>
      )}
    </Tooltip>
  );
};
